import {List, type Map as ImmutableMap} from 'immutable';
import {stats} from 'lib/StatsUtil';
import {kebabCase} from 'lodash';
import constants from 'client/constants';
import type {GuideModelV1} from 'resources/augmented/Guide/GuideModel.v1';

type Location = {
  path: string;
  host: string;
  href: string;
};

/**
 * Subset of the Subject meta model methods these extensions rely on. The values
 * are dynamic JSON:API meta fields surfaced as numbers/booleans by the model.
 */
interface SubjectMeta {
  isUserHasAccess(): boolean;
  getCountOfEasyGuesses(): number;
  getCountOfMediumGuesses(): number;
  getCountOfDifficultGuesses(): number;
  getCountOfEasyCorrectGuesses(): number;
  getCountOfMediumCorrectGuesses(): number;
  getCountOfDifficultCorrectGuesses(): number;
  getSumOfEasyGuessesPointsEarned(): number;
  getSumOfMediumGuessesPointsEarned(): number;
  getSumOfDifficultGuessesPointsEarned(): number;
}

/**
 * Shape of the model instance these extensions are mixed into. The extensions
 * rely on methods provided by the base SubjectModel.
 */
interface SubjectContext {
  getName(): string;
  getValueForAttribute(attribute: string): unknown;
  getMeta(): SubjectMeta;
  getLogoImageUrl(): string;
  getCurriculumArea(): {
    isEmpty(): boolean;
    getName(): string;
  };
  getGuides(): List<GuideModelV1>;
  getIn(path: Array<string>, notSetValue: number): number;
  // Sibling extension methods invoked via `this` from other extensions.
  getTotalCorrectAnswers(): number;
  getTotalAnswered(): number;
  getCorrectAnswerCountByDifficulty(difficulty: string): number;
  getTotalAnsweredCountByDifficulty(difficulty: string): number;
  getTotalTimeElapsedByDifficulty(difficulty: string): number;
  getQuestionCountByDifficulty(difficulty: string): number;
}

export const subjectExtensions: ThisType<SubjectContext> = {
  isAPSubject(): boolean {
    return this.getName().startsWith('AP');
  },
  doesUserHaveAccess(): boolean {
    return this.getMeta().isUserHasAccess();
  },
  /**
   * Overrides the generated getter, which returns the raw attribute (null when
   * subscriptions_prices is NULL). Normalize to an empty List so every caller can
   * safely call List methods (.first(), .map()) without crashing on a price-less
   * subject.
   */
  getSubscriptionsPrices(): List<ImmutableMap<string, unknown>> {
    const value = this.getValueForAttribute('subscriptions_prices') as
      | List<ImmutableMap<string, unknown>>
      | null
      | undefined;
    return value ?? List();
  },
  /**
   * Returns a `Location` object representing the location of the `logo_image_url`
   *
   * @returns {Object}
   */
  getLogoLocation(): Location {
    return {
      path: this.getLogoImageUrl(),

      host: constants.SUPPLEMENTS_ORIGIN,
      href: `${constants.SUPPLEMENTS_ORIGIN}/${this.getLogoImageUrl()}`
    };
  },
  getCurriculumAreaSlug(): string {
    const slug = !this.getCurriculumArea().isEmpty()
      ? kebabCase(this.getCurriculumArea().getName())
      : 'default';
    return slug;
  },

  getAverageAccuracy(): number {
    return stats.percentage(this.getTotalCorrectAnswers(), this.getTotalAnswered());
  },

  getAverageAccuracyByDifficulty(difficulty: string): number {
    return stats.percentage(
      this.getCorrectAnswerCountByDifficulty(difficulty),
      this.getTotalAnsweredCountByDifficulty(difficulty)
    );
  },

  getAverageTimeByDifficulty(difficulty: string): number {
    return stats.divide(
      this.getTotalTimeElapsedByDifficulty(difficulty),
      this.getTotalAnsweredCountByDifficulty(difficulty),
      {fixed: false}
    );
  },

  getCompletionPercentageByDifficulty(difficulty: string): number {
    return stats.percentage(
      this.getTotalAnsweredCountByDifficulty(difficulty),
      this.getQuestionCountByDifficulty(difficulty)
    );
  },

  getPracticeGuide(): GuideModelV1 {
    return this.getGuides().find((guide) => guide?.getGuideType() === 'practice');
  },

  getGuideTypes(): Array<GuideModelV1> {
    return this.getGuides().reduce<Array<GuideModelV1>>((acc, guide) => {
      acc?.push(guide?.getGuideType());
      return acc as Array<GuideModelV1>;
    }, []);
  },

  getQuestionCountByDifficulty(difficulty: string): number {
    return this.getIn(['meta', `count_of_${difficulty}_questions`], 0);
  },

  getTotalAnswered(): number {
    const total =
      this.getMeta().getCountOfEasyGuesses() +
      this.getMeta().getCountOfMediumGuesses() +
      this.getMeta().getCountOfDifficultGuesses();
    return isNaN(total) ? 0 : total;
  },

  getTotalCorrectAnswers(): number {
    const total =
      this.getMeta().getCountOfEasyCorrectGuesses() +
      this.getMeta().getCountOfMediumCorrectGuesses() +
      this.getMeta().getCountOfDifficultCorrectGuesses();
    return isNaN(total) ? 0 : total;
  },

  getTotalPointsEarned(): number {
    const total =
      this.getMeta().getSumOfEasyGuessesPointsEarned() +
      this.getMeta().getSumOfMediumGuessesPointsEarned() +
      this.getMeta().getSumOfDifficultGuessesPointsEarned();
    return isNaN(total) ? 0 : total;
  },

  getTotalPointsEarnedByDifficulty(difficulty: string): number {
    return this.getIn(['meta', `sum_of_${difficulty}_guesses_points_earned`], 0);
  },

  getCorrectAnswerCountByDifficulty(difficulty: string): number {
    return this.getIn(['meta', `count_of_${difficulty}_correct_guesses`], 0);
  },

  getTotalAnsweredCountByDifficulty(difficulty: string): number {
    return this.getIn(['meta', `count_of_${difficulty}_guesses`], 0);
  },

  getTotalTimeElapsedByDifficulty(difficulty: string): number {
    return this.getIn(['meta', `sum_of_${difficulty}_guesses_time_elapsed`], 0);
  }
};
